import Phaser from '../lib/phaser.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { UI_ASSET_KEYS } from '../assets/asset-keys.js';
import { DIRECTION } from '../common/direction.js';
import { NineSlice } from '../utils/nine-slice.js';
import { BaseScene } from './base-scene.js';
import { SCENE_KEYS } from './scene-keys.js';
import { dataManager } from '../utils/data-manager.js';
import { netManager } from '../net/index.js';
import {
  fetchMarketListings,
  fetchShopItems,
  fetchVaultStatus,
  buyShopItem,
  claimGoldVault,
  fetchDailyStatus,
  claimDailyReward,
} from '../net/api.js';
import { DEMO_MARKET_LISTINGS, ITEM_DISPLAY_NAMES } from '../net/demo-content.js';
import { makeButton, makeCloseButton } from '../utils/ui-button.js';

// ── Marketplace panel: Gold Item Shop (buy SYSTEM items with Gold) + read-only $TOKEN board. ──
// The Gold Item Shop is the headline: a fixed catalog of system items the player buys with in-game
// Gold (server-validated via POST /shop/buy-item → spendGold). The standout is the Gold Vault, a
// one-time unlock that lets the player claim +100 Gold once per real 24h — a long-term sink, with
// the cooldown enforced SERVER-SIDE (a tampered client can't claim early or for more). The legacy
// P2P $TOKEN listings still render read-only below. Degrades gracefully offline (no api → demo
// catalog, buttons no-op with a calm message). Buttons fire on POINTER_DOWN (POINTER_UP is
// unreliable inside these paused/overlay scenes).

/** @typedef {import('@mn/shared').MarketListing} MarketListing */
/** @typedef {import('../net/api.js').ShopItem} ShopItem */
/** @typedef {import('../net/api.js').VaultStatus} VaultStatus */

/**
 * @typedef MarketplaceSceneData
 * @type {object}
 * @property {string} previousSceneName
 */

/** Offline fallback catalog so the shop renders without an api (mirrors @mn/shared SHOP_ITEMS). */
const DEMO_SHOP_ITEMS = Object.freeze([
  { id: 'si_greater_potion', name: 'Greater Potion', description: 'Heal a monster ~60 HP.', priceGold: 250, effect: { kind: 'inventory' } },
  { id: 'si_revive', name: 'Revive', description: 'Revive one fainted monster.', priceGold: 450, effect: { kind: 'inventory' } },
  { id: 'si_premium_ball_5', name: 'Premium Capture Ball (x5)', description: '5 balls with a much better catch rate.', priceGold: 400, effect: { kind: 'inventory' } },
  { id: 'si_lucky_charm', name: 'Lucky Charm', description: '+10% battle Gold for the next 20 battles.', priceGold: 1200, effect: { kind: 'buff' } },
  { id: 'si_gold_vault', name: 'Gold Vault', description: 'One-time unlock: claim +100 Gold once every 24h.', priceGold: 3000, effect: { kind: 'gold_vault' } },
]);

const TITLE_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#ffffff', fontSize: '32px' };
const SECTION_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#205030', fontSize: '20px' };
const NAME_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#0b2a18', fontSize: '20px' };
const DESC_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#3a5a47', fontSize: '14px' };
const PRICE_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#a06a00', fontSize: '18px' };
const ROW_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#000000', fontSize: '18px' };
const HINT_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#cfe8ff', fontSize: '18px' };
const GOLD_STYLE = { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, color: '#f5b23e', fontSize: '20px' };

const ITEM_ROW_HEIGHT = 56;

export class MarketplaceScene extends BaseScene {
  /** @type {MarketplaceSceneData} */
  #sceneData;
  /** @type {ShopItem[]} */
  #items = [];
  /** @type {VaultStatus | null} */
  #vault = null;
  /** @type {import('../net/api.js').DailyStatus | null} */
  #daily = null;
  /** @type {Phaser.GameObjects.Container} */
  #dailyContainer;
  /** @type {MarketListing[]} */
  #listings = [];
  /** @type {Phaser.GameObjects.Container} */
  #shopContainer;
  /** @type {Phaser.GameObjects.Container} */
  #listContainer;
  /** @type {Phaser.GameObjects.Text} */
  #statusText;
  /** @type {Phaser.GameObjects.Text} */
  #goldText;
  /** @type {boolean} prevents double-submits while a buy/claim is in-flight */
  #busy = false;

  constructor() {
    super({ key: SCENE_KEYS.MARKETPLACE_SCENE });
  }

  /**
   * @param {MarketplaceSceneData} data
   * @returns {void}
   */
  init(data) {
    super.init(data);
    this.#sceneData = data;
    this.#items = [];
    this.#vault = null;
    this.#daily = null;
    this.#listings = [];
    this.#busy = false;
  }

  /** @returns {void} */
  create() {
    super.create();
    this._claimOverlayInput();

    // Oversized, centered scrim so it still covers the view once the camera zooms out to fit the
    // fixed 800px design on a narrow phone (see #fitToViewport).
    this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width * 4, this.scale.height * 4, 0x05070f, 0.86).setOrigin(0.5);

    const panelW = 800;
    const panelH = 540;
    const panel = new NineSlice({
      cornerCutSize: 32,
      textureManager: this.sys.textures,
      assetKeys: [UI_ASSET_KEYS.MENU_BACKGROUND_GREEN],
    });
    const container = panel
      .createNineSliceContainer(this, panelW, panelH, UI_ASSET_KEYS.MENU_BACKGROUND_GREEN)
      .setPosition(this.scale.width / 2 - panelW / 2, 40);
    const bg = this.add.rectangle(4, 4, panelW - 8, panelH - 8, 0xeafff0).setOrigin(0).setAlpha(0.7);
    container.add(bg);

    this.add.text(this.scale.width / 2, 24, 'Marketplace', TITLE_STYLE).setOrigin(0.5);

    // Gold balance (top-right of panel) so the player sees what they can afford.
    this.#goldText = this.add
      .text(this.scale.width / 2 + panelW / 2 - 24, 60, `${dataManager.getGold()} G`, GOLD_STYLE)
      .setOrigin(1, 0.5);

    makeCloseButton(this, this.scale.width / 2 + panelW / 2 - 14, 70, () => this.#close());

    // Section header for the Gold Item Shop.
    const hx = this.scale.width / 2 - panelW / 2 + 36;
    this.add.text(hx, 84, 'GOLD ITEM SHOP', SECTION_STYLE);

    this.#shopContainer = this.add.container(0, 0);
    this.#listContainer = this.add.container(0, 0);
    this.#dailyContainer = this.add.container(0, 0);

    // Status line: anchored to the DESIGN space (just below the 540px panel) rather than the real
    // viewport bottom, so it scales/centers correctly with the responsive camera zoom below.
    this.#statusText = this.add
      .text(this.scale.width / 2, 40 + panelH + 18, 'Loading shop…', HINT_STYLE)
      .setOrigin(0.5);

    // Fit the fixed 800×540 design into the real viewport (phones are far narrower than 800px),
    // centered, so nothing overflows. Re-runs on resize/orientation change.
    const onResize = () => this.#fitToViewport(panelW, panelH);
    this.#fitToViewport(panelW, panelH);
    this.scale.on(Phaser.Scale.Events.RESIZE, onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, onResize, this);
    });

    void this.#load();
  }

  /**
   * CONTAIN-fit the fixed-size marketplace design into the actual viewport via a centered camera
   * zoom, so the 800px-wide panel never runs off a narrow phone screen. The whole layout is built
   * symmetrically around `this.scale.width/2`, so a uniform zoom about that center keeps everything
   * on-screen. The dark scrim is drawn at real size and counter-scaled so it always covers the view.
   * @param {number} designW
   * @param {number} designH
   * @returns {void}
   */
  #fitToViewport(designW, designH) {
    const vw = this.scale.width;
    const vh = this.scale.height;
    const cam = this.cameras.main;
    // design bounds: x spans [cx-400, cx+400]; y spans ~[20, 40+540+40]. Add a small margin.
    const designTotalH = 40 + designH + 48;
    const zoom = Math.min(1, (vw - 16) / designW, vh / designTotalH);
    cam.setZoom(zoom);
    cam.centerOn(vw / 2, 40 + designH / 2);
    cam.setBackgroundColor('#05070f');
  }

  /** @returns {Promise<void>} */
  async #load() {
    const wallet = netManager?.wallet ?? null;
    const [items, vault, daily, listings] = await Promise.all([
      fetchShopItems(),
      fetchVaultStatus(wallet),
      fetchDailyStatus(wallet),
      fetchMarketListings(),
    ]);
    this.#items = items.length > 0 ? items : DEMO_SHOP_ITEMS.slice();
    this.#vault = vault;
    this.#daily = daily;
    this.#listings = (listings ?? []).filter((l) => l.status === 'open');
    if (this.#listings.length === 0) this.#listings = DEMO_MARKET_LISTINGS.slice();
    this.#statusText.setText('Buy with Gold · click Buy · ESC close');
    this.#renderDaily();
    this.#renderShop();
    this.#renderListings();
  }

  /** Render the always-reachable "🎁 Daily Reward" claim strip (top-right of the panel header). */
  #renderDaily() {
    this.#dailyContainer.removeAll(true);
    if (!this.#daily) return; // offline / walletless → no daily strip
    const ready = this.#daily.canClaim;
    const rewardLabel = this.#daily.reward?.label ?? 'reward';
    const label = ready ? 'CLAIM' : `Next in ${this.#fmtWait(this.#daily.msUntilNext)}`;
    const cx = this.scale.width / 2;
    const panelRight = cx + 400;

    this.#dailyContainer.add(
      this.add.text(panelRight - 320, 84, '🎁 DAILY REWARD', SECTION_STYLE).setOrigin(0, 0)
    );
    // The rotating reward up next (e.g. "Today: 3 Capture Balls").
    this.#dailyContainer.add(
      this.add
        .text(panelRight - 320, 104, `${ready ? 'Today' : 'Next'}: ${rewardLabel}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#aef0c4',
        })
        .setOrigin(0, 0)
    );
    this.#dailyContainer.add(
      makeButton(this, {
        x: panelRight - 120,
        y: 94,
        width: 180,
        height: 36,
        label,
        primary: ready,
        fontSize: 17,
        onClick: () => {
          if (ready) void this.#claimDaily();
        },
      })
    );
  }

  /** Claim the free daily login reward (server-enforced 24h cooldown), sync Gold + UI. */
  async #claimDaily() {
    if (this.#busy) return;
    const wallet = netManager?.wallet ?? null;
    if (!wallet) {
      this.#statusText.setText('Connect your wallet to claim.');
      return;
    }
    this.#busy = true;
    this.#statusText.setText('Claiming daily reward…');
    const res = await claimDailyReward(wallet);
    this.#busy = false;
    if (res.ok) {
      dataManager.setGold(res.gold);
      this.#refreshGold();
      // Item rewards (capture balls / potions) → add the stack to the local Bag so it shows now.
      if (res.grantedItem) {
        dataManager.addItem({ id: res.grantedItem.itemId }, res.grantedItem.qty);
      }
      this.#statusText.setText(`Claimed ${res.label}!`);
      this.#daily = { canClaim: false, msUntilNext: 24 * 60 * 60 * 1000 };
    } else if (res.reason === 'cooldown') {
      this.#statusText.setText(`Daily reward on cooldown — ${this.#fmtWait(res.msUntilNext)} left.`);
      this.#daily = { canClaim: false, msUntilNext: res.msUntilNext };
    } else {
      this.#statusText.setText('Claim failed — try again.');
    }
    this.#renderDaily();
  }

  /** @returns {void} */
  #refreshGold() {
    this.#goldText?.setText(`${dataManager.getGold()} G`);
  }

  /** Format a remaining-cooldown ms value as a short "Xh Ym" / "Ym" label. */
  #fmtWait(ms) {
    const totalMin = Math.ceil(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  /** Render the Gold Item Shop catalog with per-row Buy buttons + the Gold Vault claim. */
  #renderShop() {
    this.#shopContainer.removeAll(true);
    const hx = this.scale.width / 2 - 400 + 36;
    // Start the catalog below the header strip so the first row's Buy button clears the right-aligned
    // "Daily Reward" claim button (they used to clip each other — "buttons overlapping").
    const startY = 136;

    this.#items.forEach((item, i) => {
      const y = startY + i * ITEM_ROW_HEIGHT;

      this.#shopContainer.add(
        this.add.rectangle(hx - 10, y - 8, 728, ITEM_ROW_HEIGHT - 8, 0x18b85f, 0.06).setOrigin(0, 0)
      );
      this.#shopContainer.add(this.add.text(hx, y, item.name, NAME_STYLE));
      this.#shopContainer.add(this.add.text(hx, y + 24, item.description, DESC_STYLE));
      this.#shopContainer.add(
        this.add.text(hx + 470, y + 10, `${item.priceGold} G`, PRICE_STYLE).setOrigin(1, 0.5)
      );

      const isVaultOwned = item.effect?.kind === 'gold_vault' && this.#vault?.owned;
      this.#shopContainer.add(
        makeButton(this, {
          x: hx + 560,
          y: y + 10,
          width: 120,
          height: 38,
          label: isVaultOwned ? 'Owned' : 'Buy',
          primary: !isVaultOwned,
          fontSize: 18,
          onClick: () => {
            if (!isVaultOwned) void this.#buy(item);
          },
        })
      );
    });

    this.#renderVaultClaim(startY + this.#items.length * ITEM_ROW_HEIGHT + 8);
  }

  /** Render the "Claim daily (+100)" Gold Vault button + cooldown state (only when owned). */
  #renderVaultClaim(y) {
    if (!this.#vault?.owned) return;
    const hx = this.scale.width / 2 - 400 + 36;
    const ready = (this.#vault.msUntilNextClaim ?? 0) <= 0;
    const label = ready ? 'Claim daily (+100 G)' : `Next claim in ${this.#fmtWait(this.#vault.msUntilNextClaim)}`;

    this.#shopContainer.add(this.add.text(hx, y, 'GOLD VAULT', SECTION_STYLE));
    this.#shopContainer.add(
      makeButton(this, {
        x: hx + 200,
        y: y + 10,
        width: 240,
        height: 40,
        label,
        primary: ready,
        fontSize: 18,
        onClick: () => {
          if (ready) void this.#claimVault();
        },
      })
    );
  }

  /**
   * Buy a catalog item (server validates affordability + applies the effect), sync Gold + UI.
   * @param {ShopItem} item
   */
  async #buy(item) {
    if (this.#busy) return;
    const wallet = netManager?.wallet ?? null;
    if (!wallet) {
      this.#statusText.setText('Connect your wallet to buy items.');
      return;
    }
    this.#busy = true;
    this.#statusText.setText(`Buying ${item.name}…`);
    const res = await buyShopItem(wallet, item.id);
    this.#busy = false;
    if (res.ok) {
      dataManager.setGold(res.gold);
      this.#refreshGold();
      // Add the purchased stack to the LOCAL Bag immediately. Without this the gold was spent + the
      // item was granted server-side, but the player's Bag stayed empty until a relog (reported bug).
      if (res.grantedItem?.itemId) {
        dataManager.addItem({ id: res.grantedItem.itemId }, res.grantedItem.qty || 1);
      }
      this.#statusText.setText(res.granted || `Bought ${item.name}.`);
      if (item.effect?.kind === 'gold_vault') {
        // Refresh vault state so the "Claim daily" button appears immediately.
        this.#vault = (await fetchVaultStatus(wallet)) ?? { owned: true, claimedAt: null, msUntilNextClaim: 0 };
        this.#renderShop();
      }
    } else if (res.reason === 'insufficient') {
      this.#statusText.setText('Not enough Gold for that.');
    } else {
      this.#statusText.setText('Purchase failed — try again.');
    }
  }

  /** Claim the daily Gold Vault payout (server-enforced 24h cooldown). */
  async #claimVault() {
    if (this.#busy) return;
    const wallet = netManager?.wallet ?? null;
    if (!wallet) {
      this.#statusText.setText('Connect your wallet to claim.');
      return;
    }
    this.#busy = true;
    this.#statusText.setText('Claiming Gold Vault…');
    const res = await claimGoldVault(wallet);
    this.#busy = false;
    if (res.ok) {
      dataManager.setGold(res.gold);
      this.#refreshGold();
      this.#statusText.setText('Claimed +100 Gold!');
      this.#vault = { owned: true, claimedAt: new Date().toISOString(), msUntilNextClaim: 24 * 60 * 60 * 1000 };
    } else if (res.reason === 'cooldown') {
      this.#statusText.setText(`Vault on cooldown — ${this.#fmtWait(res.msUntilNextClaim)} left.`);
      this.#vault = { owned: true, claimedAt: this.#vault?.claimedAt ?? null, msUntilNextClaim: res.msUntilNextClaim };
    } else {
      this.#statusText.setText('Claim failed — try again.');
    }
    this.#renderShop();
  }

  /**
   * @param {MarketListing} l
   * @returns {string}
   */
  #labelFor(l) {
    switch (l.kind) {
      case 'gold':
        return 'Gold';
      case 'item':
        return ITEM_DISPLAY_NAMES[l.refId] ?? `Item: ${l.refId ?? '?'}`;
      case 'monster': {
        const ref = (l.refId ?? '?').replace(/^sp_/, '');
        return `Monster: ${ref.charAt(0).toUpperCase()}${ref.slice(1)}`;
      }
      default:
        return l.kind;
    }
  }

  /** Render the legacy read-only $TOKEN P2P board below the Gold shop (compact, top 3). */
  #renderListings() {
    this.#listContainer.removeAll(true);
    const hx = this.scale.width / 2 - 400 + 36;
    const headerY = 136 + this.#items.length * ITEM_ROW_HEIGHT + (this.#vault?.owned ? 56 : 8);

    this.#listContainer.add(this.add.text(hx, headerY, 'P2P LISTINGS ($TOKEN, read-only)', SECTION_STYLE));
    this.#listings.slice(0, 3).forEach((l, i) => {
      const y = headerY + 28 + i * 26;
      this.#listContainer.add(this.add.text(hx, y, `${this.#labelFor(l)}  x${l.qty}`, ROW_STYLE));
      this.#listContainer.add(
        this.add.text(hx + 470, y, `${l.priceToken} $TOKEN`, { ...ROW_STYLE, color: '#0a6d52' }).setOrigin(1, 0)
      );
    });
  }

  /** @returns {void} */
  update() {
    super.update();
    if (this._controls.isInputLocked) return;
    if (this._controls.wasBackKeyPressed()) {
      this.#close();
      return;
    }
    // Keyboard convenience: UP/DOWN does nothing destructive here (mouse-driven shop), kept for
    // back-compat with the overlay input contract.
    void DIRECTION;
  }

  /** @returns {void} */
  #close() {
    this._controls.lockInput = true;
    this.scene.stop(SCENE_KEYS.MARKETPLACE_SCENE);
    this.scene.resume(this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE);
  }
}
